import { cva, VariantProps } from "class-variance-authority";
import React from "react";

interface Props {
  className?: string;
  children: string | React.ReactNode;
  as?: React.ElementType;
}

const H1: React.FC<Props> = ({ className, children, as }) => {
  const Component = as || "h1";
  return (
    <Component
      className={`scroll-m-20 text-1.5xl sm:text-3.5xl leading-7 sm:leading-10 text-gray-800 font-semibold tracking-normal ${className}`}
    >
      {children}
    </Component>
  );
};

const H2: React.FC<Props> = ({ className, children, as }) => {
  const Component = as || "h2";
  return (
    <Component
      className={`scroll-m-20 text-1.5xl sm:text-2.5xl text-gray-800 font-semibold first:mt-0 tracking-tight ${className}`}
    >
      {children}
    </Component>
  );
};

const H3: React.FC<Props> = ({ className, children, as }) => {
  const Component = as || "h3";
  return (
    <Component
      className={`scroll-m-20 text-1.5xl sm:text-2.25xl font-semibold text-gray-800 tracking-tight ${className}`}
    >
      {children}
    </Component>
  );
};

const H4: React.FC<Props> = ({ className, children, as }) => {
  const Component = as || "h4";

  return (
    <Component
      className={`scroll-m-20 text-lg sm:text-1.5xl font-semibold text-gray-800 tracking-normal ${className}`}
    >
      {children}
    </Component>
  );
};

const H5: React.FC<Props> = ({ className, children, as }) => {
  const Component = as || "h5";
  return (
    <Component
      className={`scroll-m-20 text-lg tracking-normal text-gray-800 ${className}`}
    >
      {children}
    </Component>
  );
};

const P: React.FC<Props> = ({ className, children, as }) => {
  // Leading 4.25 (17.5px in design) will cause text cut-off
  const Component = as || "p";
  return (
    <Component className={`${className} text-gray-800 leading-5`}>
      {children}
    </Component>
  );
};

const Blockquote: React.FC<Props> = ({ className, children }) => {
  return (
    <blockquote className={`mt-6 border-l-2 pl-6 italic ${className}`}>
      {children}
    </blockquote>
  );
};

const InlineCode: React.FC<Props> = ({ className, children }) => {
  return (
    <code
      className={`relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold ${className}`}
    >
      {children}
    </code>
  );
};

const Lead: React.FC<Props> = ({ className, children }) => {
  return (
    <p className={`text-xl text-muted-foreground ${className}`}>{children}</p>
  );
};

const Large: React.FC<Props> = ({ className, children }) => {
  return <div className={`text-lg font-semibold ${className}`}>{children}</div>;
};

const Muted: React.FC<Props> = ({ className, children }) => {
  return <p className={`text-muted-foreground ${className}`}>{children}.</p>;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const bodyVariants = cva(
  // "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  "leading-7 text-gray-800",
  {
    variants: {
      variant: {
        xs: "text-2xs sm:text-xs",
        sm: "text-sm sm:text-sm",
        base: "text-base",
        lg: "text-sm sm:text-lg",
        xl: "text-sm sm:text-xl",
      },
      muted: {
        true: "text-gray-500",
      },
    },
    defaultVariants: {
      variant: "sm",
      muted: false,
    },
  }
);

export interface BodyProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof bodyVariants> {}

export { Blockquote, H1, H2, H3, H4, H5, InlineCode, Large, Lead, Muted, P };
